import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  ViewChild,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import {
  outputToObservable,
  takeUntilDestroyed,
} from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import {
  RdsAlertComponent,
  RdsBadgeComponent,
  RdsButtonComponent,
  RdsDialogService,
  RdsIconComponent,
  RdsLinkComponent,
  RdsProgressSpinnerComponent,
  RdsTabComponent,
  RdsTabsComponent,
} from '@rds/angular-components';
import { filter, switchMap, take } from 'rxjs';

import {
  BookingBasicData,
  BookingStatusEnum,
  ChatMessageTypeEnum,
  HasAccessDirective,
  MessageService,
  MessagesComponent,
  NavigationService,
  NotificationSourceTypeEnum,
  Permission,
  RoleType,
  RoomBookingsCalendarComponent,
} from '@bookly/shared';

import { BookingHistoryComponent } from '../../components/booking-history/booking-history.component';
import {
  BookingRequestActionModalComponent,
  BookingRequestActionModalEnum,
} from '../../components/booking-request-action-modal/booking-request-action-modal.component';
import { EventDetailsComponent } from '../../components/event-details/event-details.component';
import { BookingRequestService } from '../../services/booking-request.service';

@Component({
  selector: 'bad-booking-request-details',
  templateUrl: './booking-request-details.component.html',
  styleUrls: ['./booking-request-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RdsBadgeComponent,
    RdsIconComponent,
    RdsLinkComponent,
    EventDetailsComponent,
    MessagesComponent,
    RoomBookingsCalendarComponent,
    HasAccessDirective,
    RdsTabsComponent,
    RdsTabComponent,
    RdsProgressSpinnerComponent,
    RdsButtonComponent,
    RdsAlertComponent,
    BookingHistoryComponent,
  ],
})
export class BookingRequestDetailsComponent implements OnInit {
  readonly #dialogService = inject(RdsDialogService);
  readonly #bookingRequestService = inject(BookingRequestService);
  readonly #messageService = inject(MessageService);
  readonly #destroyRef = inject(DestroyRef);
  readonly #activatedRoute = inject(ActivatedRoute);
  readonly #router = inject(Router);
  readonly #navigationService = inject(NavigationService);

  protected bookingData!: BookingBasicData;
  protected readonly bookingDetails =
    this.#bookingRequestService.bookingDetails;
  protected readonly bookingHistory =
    this.#bookingRequestService.bookingHistory;
  protected readonly isBookingHistoryLoading =
    this.#bookingRequestService.isBookingHistoryLoading;
  protected readonly chatMessages = this.#messageService.chatMessages;
  protected readonly workNotes = this.#messageService.workNotes;
  protected readonly isLoading =
    this.#bookingRequestService.isDetailsDataLoading;
  protected readonly isMessageLoading = this.#messageService.isMessageLoading;
  protected readonly BookingStatusEnum = BookingStatusEnum;
  protected readonly BookingRequestActionModalEnum =
    BookingRequestActionModalEnum;
  protected readonly NotificationSourceTypeEnum = NotificationSourceTypeEnum;
  protected readonly isEditMode = signal(false);
  protected eventDateTime: Date | null = null;
  protected readonly RoleType = RoleType;
  protected readonly Permission = Permission;
  protected role: RoleType | undefined = undefined;
  protected readonly ChatMessageTypeEnum = ChatMessageTypeEnum;

  @ViewChild('messagesTab', { static: false })
  protected messagesTab!: RdsTabComponent;

  protected rdsTabs?: RdsTabsComponent;

  /**
   * rds-tab only toggles [hidden], so this instance survives tab switches and
   * the action bar can drive its edit / save / cancel from anywhere.
   */
  protected readonly eventDetails = viewChild(EventDetailsComponent);

  /** Offering Edit outside the Detail tab would jump to a form nobody can see. */
  protected readonly isDetailTabActive = signal(true);

  @ViewChild(RdsTabsComponent, { static: false })
  set tabsWrapper(component: RdsTabsComponent) {
    this.rdsTabs = component;
    if (!this.rdsTabs || !this.messagesTab) {
      return;
    }
    if (this.rdsTabs) {
      outputToObservable(this.rdsTabs.selectedTabChange)
        .pipe(takeUntilDestroyed(this.#destroyRef))
        .subscribe(change => this.isDetailTabActive.set(change.index === 0));
      outputToObservable(this.rdsTabs.selectedTabChange)
        .pipe(
          filter(change => change.tab === this.messagesTab),
          filter(() =>
            this.chatMessages().some(message => !message.readByViewer)
          ),
          switchMap(() =>
            this.#messageService.markAllMessagesAsRead(
              this.bookingData.bookingId
            )
          ),
          takeUntilDestroyed(this.#destroyRef)
        )
        .subscribe();
    }
  }

  public ngOnInit() {
    this.#activatedRoute.paramMap
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(params => {
        this.setEditMode(false);
        this.bookingData = {
          bookingId: Number(params.get('bookingId')),
          roomDexId: params.get('roomDexId') || '',
          role: this.#activatedRoute.snapshot.data['role'] as RoleType,
          setActiveTab: this.#activatedRoute.snapshot.queryParams[
            'setActiveTab'
          ] as NotificationSourceTypeEnum,
        };
        this.role = this.bookingData.role;
        this.#bookingRequestService
          .getBookingRequestData(
            this.bookingData.roomDexId,
            this.bookingData.bookingId
          )
          .subscribe();
        this.#messageService
          .getMessages(this.bookingData.bookingId, ChatMessageTypeEnum.Chat)
          .subscribe();
        this.#messageService
          .getMessages(this.bookingData.bookingId, ChatMessageTypeEnum.WorkNote)
          .subscribe();
        this.#bookingRequestService
          .loadBookingHistory(this.bookingData.bookingId)
          .subscribe();
      });
  }

  protected goBack() {
    // When opened directly (e.g. page refresh) there is no navigation history,
    // so fall back to the bookings list (current URL without the two detail
    // params) instead of the app root.
    const listUrl = this.#router.url
      .split('?')[0]
      .split('/')
      .slice(0, -2)
      .join('/');
    this.#navigationService.back([listUrl]);
  }

  protected openModal(action: BookingRequestActionModalEnum) {
    const dialogRef = this.#dialogService.open(
      BookingRequestActionModalComponent,
      {
        size: 's',
        data: action,
      }
    );
    dialogRef
      .afterClosed()
      .pipe(
        filter(Boolean),
        take(1),
        switchMap(dialogData => {
          if (action === BookingRequestActionModalEnum.Confirm) {
            return this.#bookingRequestService.approveBookingRequest(
              this.bookingData.bookingId,
              dialogData.message
            );
          }

          if (action === BookingRequestActionModalEnum.Cancel) {
            return this.#bookingRequestService.cancelBookingRequest(
              this.bookingData.bookingId,
              dialogData.message
            );
          }

          return this.#bookingRequestService.rejectBookingRequest(
            this.bookingData.bookingId,
            dialogData.message
          );
        }),
        switchMap(() =>
          this.#bookingRequestService.loadBookingHistory(
            this.bookingData.bookingId
          )
        )
      )
      .subscribe();
  }

  protected setEditMode(isEditMode: boolean) {
    this.isEditMode.set(isEditMode);
  }

  protected postMessage(message: string, type: ChatMessageTypeEnum) {
    this.#messageService
      .postMessage(
        {
          bookingId: this.bookingData.bookingId,
          message,
        },
        type
      )
      .subscribe();
  }

  protected fillEventDate(date: Date) {
    this.eventDateTime = date;
    if (this.rdsTabs && this.isEditMode()) {
      this.rdsTabs.selectTab(0);
    }
  }
}
